import {
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
} from '@reduxjs/toolkit';
import { RootState, ThunkApi } from 'state/store';
import { Status } from 'utils/customTypes';
import { SLICE_STATUS } from 'utils/constants';
import { EventPermissions } from 'types/store/normalized';

/* ============================== STATE SETUP =============================== */
interface EventPermissionsState {
  status: Status;
}

const initialState: EventPermissionsState = {
  status: SLICE_STATUS.IDLE,
};

const adapter = createEntityAdapter<EventPermissions>();

/* ============================== REDUX THUNK =============================== */
function concurrencyCondition(
  thunkParameters: unknown,
  { getState }: { getState: () => RootState }
): boolean {
  const { status } = getState().normalized.eventPermissions;
  return status !== SLICE_STATUS.LOADING;
}

interface GetListParams {
  scheduleIds?: string[];
}

const NO_OP_RESPONSE: EventPermissions[] = [];

export const getEventPermissions = createAsyncThunk<
  EventPermissions[],
  GetListParams,
  ThunkApi
>(
  'normalized/eventPermissions/getEventPermissions',
  async (
    { scheduleIds = [] },
    { getState, extra: { getEventPermissionsApi } }
  ) => {
    const state = getState();

    const cleanedIds = scheduleIds.filter(
      (id): id is string => typeof id === 'string' && id.trim() !== ''
    );
    const notCached = cleanedIds.filter(
      (id) => !state.normalized.eventPermissions.entities[id]
    );

    if (notCached.length === 0) {
      return NO_OP_RESPONSE;
    }

    const { data } = await getEventPermissionsApi().getByIdsPost({
      scheduleIds: notCached,
    });

    return data;
  },
  { condition: concurrencyCondition }
);

/* ================================= REDUCER ================================ */
const eventPermissionsSlice = createSlice({
  name: 'normalized/eventPermissions',
  initialState: adapter.getInitialState(initialState),
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getEventPermissions.pending, (state) => {
        state.status = SLICE_STATUS.LOADING;
      })
      .addCase(getEventPermissions.rejected, (state) => {
        state.status = SLICE_STATUS.FAILED;
      })
      .addCase(getEventPermissions.fulfilled, (state, action) => {
        adapter.upsertMany(state, action?.payload ?? []);

        state.status = SLICE_STATUS.SUCCESS;
      });
  },
});

/* =============================== SELECTORS ================================ */
const defaultSelectors = adapter.getSelectors<RootState>(
  (state) => state.normalized.eventPermissions
);

const selectStatus = (state: RootState) =>
  state.normalized.eventPermissions.status;

const selectEventPermissionsById = (state: RootState) => (id: string) =>
  defaultSelectors.selectById(state, id);

export const eventPermissionsSelectors = {
  ...defaultSelectors,
  selectStatus,
  selectEventPermissionsById,
};

export default eventPermissionsSlice.reducer;
